import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, PROS } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Building2, Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = { name: "", ipi: "", pro: "" };

export default function Publishers() {
  const [publishers, setPublishers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/publishers").then(({ data }) => setPublishers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, ipi: p.ipi, pro: p.pro }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editId) await api.put(`/publishers/${editId}`, form);
      else await api.post("/publishers", form);
      toast.success(editId ? "Publisher updated" : "Publisher added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/publishers/${id}`);
    toast.success("Publisher deleted");
    load();
  };

  return (
    <div data-testid="publishers-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Rights Holders</h4>
          <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Publishers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-publisher-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" /> Add Publisher
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Publisher" : "Add Publisher"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
                <Input data-testid="publisher-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-secondary border-border" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">IPI Number</Label>
                  <Input data-testid="publisher-ipi-input" value={form.ipi} onChange={(e) => setForm({ ...form, ipi: e.target.value })} className="mt-1.5 bg-secondary border-border font-mono" placeholder="00000000000" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">PRO</Label>
                  <Select value={form.pro} onValueChange={(v) => setForm({ ...form, pro: v })}>
                    <SelectTrigger data-testid="publisher-pro-select" className="mt-1.5 bg-secondary border-border"><SelectValue placeholder="Select PRO" /></SelectTrigger>
                    <SelectContent>{PROS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-publisher-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {publishers.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No publishers yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Name</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">IPI</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">PRO</TableHead>
                <TableHead className="text-right text-zinc-500 uppercase text-xs tracking-widest">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {publishers.map((p) => (
                <TableRow key={p.id} data-testid={`publisher-row-${p.id}`} className="border-border row-in">
                  <TableCell className="font-semibold">{p.name}</TableCell>
                  <TableCell className="font-mono text-zinc-300">{p.ipi || "—"}</TableCell>
                  <TableCell>{p.pro ? <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-semibold border border-primary/30">{p.pro}</span> : "—"}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(p)} data-testid={`edit-publisher-${p.id}`} className="text-zinc-400 hover:text-primary p-2"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`delete-publisher-${p.id}`} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
